import { fetchWithAuth } from "../utils/api";
import { saveAs } from "file-saver";

const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";

/**
 * Fetch all certificate templates (optionally filtered by instituteId)
 */
export const getAllCertificateTemplates = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const url = `${serverUrl}/api/certificate/get-all${query ? `?${query}` : ""}`;
  const response = await fetchWithAuth(url, { method: "GET" });
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch certificate templates");
  }
  return data;
};

/**
 * Fetch a single certificate template by ID
 */
export const getCertificateTemplate = async (id) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/get/${id}`,
    {
      method: "GET",
    },
  );
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch certificate template");
  }
  return data;
};

/**
 * Create a new certificate template (multipart/form-data)
 */
export const addCertificateTemplate = async (formData) => {
  const response = await fetchWithAuth(`${serverUrl}/api/certificate/add`, {
    method: "POST",
    body: formData,
  });
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to create certificate template");
  }
  return data;
};

/**
 * Update an existing certificate template (multipart/form-data)
 */
export const updateCertificateTemplate = async (id, formData) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/update/${id}`,
    {
      method: "PUT",
      body: formData,
    },
  );
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to update certificate template");
  }
  return data;
};

/**
 * Delete a certificate template by ID
 */
export const deleteCertificateTemplate = async (id) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/delete/${id}`,
    {
      method: "DELETE",
    },
  );
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to delete certificate template");
  }
  return data;
};

/**
 * Generate preview certificate PNG (returns base64)
 */
export const previewCertificate = async (payload) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/generate/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to preview certificate");
  }
  return data;
};

/**
 * Issue certificates in bulk for students of a test
 */
export const issueBulkCertificates = async (payload) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/issue-bulk`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to issue certificates");
  }
  return data;
};

/**
 * Get a generated certificate PNG Blob for display/preview
 */
export const getCertificateBlob = async (issuedCertId) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/download/${issuedCertId}`,
    {
      method: "GET",
    },
  );
  if (!response) return null;
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to fetch certificate image");
  }
  return await response.blob();
};

/**
 * Download a generated certificate PNG
 */
export const downloadCertificate = async (
  issuedCertId,
  fallbackFilename = "Certificate.png",
) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/download/${issuedCertId}`,
    {
      method: "GET",
    },
  );
  if (!response) return null;
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to download certificate");
  }

  let filename = fallbackFilename;
  const disposition = response.headers.get("content-disposition");
  if (disposition && disposition.indexOf("filename=") !== -1) {
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  saveAs(blob, filename);
  return blob;
};

/**
 * Fetch issued certificates with optional filters (testId, studentId, templateId, instituteId)
 */
export const getIssuedCertificates = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const url = `${serverUrl}/api/certificate/issued${query ? `?${query}` : ""}`;
  const response = await fetchWithAuth(url, { method: "GET" });
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch issued certificates");
  }
  return data;
};

/**
 * Revoke an issued certificate
 */
export const revokeCertificate = async (id, payload) => {
  const response = await fetchWithAuth(
    `${serverUrl}/api/certificate/revoke/${id}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response) return null;
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to revoke certificate");
  }
  return data;
};

/**
 * Verify a certificate by verification code (Public, no auth needed)
 */
export const verifyCertificate = async (code) => {
  const response = await fetch(
    `${serverUrl}/api/certificate/verify/${code}`,
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to verify certificate");
  }
  return data;
};

const certificateService = {
  getAllCertificateTemplates,
  getCertificateTemplate,
  addCertificateTemplate,
  updateCertificateTemplate,
  deleteCertificateTemplate,
  previewCertificate,
  issueBulkCertificates,
  getCertificateBlob,
  downloadCertificate,
  getIssuedCertificates,
  revokeCertificate,
  verifyCertificate,
};

export default certificateService;
